import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import './liquid-transition.css';

export type LiquidTransitionDirection = 'entering' | 'returning';

export function liquidTransitionDirection(
  previousChildInstanceId: string | null,
  nextChildInstanceId: string | null,
): LiquidTransitionDirection | null {
  if (previousChildInstanceId === nextChildInstanceId) return null;
  return nextChildInstanceId === null ? 'returning' : 'entering';
}

interface ActiveLiquidTransition {
  key: number;
  direction: LiquidTransitionDirection;
  label: string;
}

export interface LiquidTransitionProps {
  childInstanceId: string | null;
  childTitle: string | null;
  reducedMotion: boolean;
  children: ReactNode;
}

/**
 * A presentation-only wipe around the authoritative play surface. The first snapshot is deliberately
 * treated as already settled, so opening or reconnecting directly into a child does not replay history.
 */
export function LiquidTransition({
  childInstanceId,
  childTitle,
  reducedMotion,
  children,
}: LiquidTransitionProps) {
  const previousChildInstanceId = useRef(childInstanceId);
  const sequence = useRef(0);
  const [active, setActive] = useState<ActiveLiquidTransition | null>(null);

  useLayoutEffect(() => {
    const direction = liquidTransitionDirection(previousChildInstanceId.current, childInstanceId);
    previousChildInstanceId.current = childInstanceId;
    if (!direction || reducedMotion) {
      setActive(null);
      return;
    }

    sequence.current += 1;
    setActive({
      key: sequence.current,
      direction,
      label: direction === 'returning' ? 'Momentum' : (childTitle ?? 'Activity'),
    });
  }, [childInstanceId, childTitle, reducedMotion]);

  return (
    <div className="liquid-transition-host">
      {children}
      {active ? (
        <div
          key={active.key}
          className={`liquid-transition liquid-transition--${active.direction}`}
          role="status"
          aria-live="polite"
          aria-label={active.direction === 'returning' ? 'Returning to Momentum' : `Opening ${active.label}`}
        >
          <div
            className="liquid-transition__sheet"
            onAnimationEnd={(event) => {
              if (event.currentTarget === event.target) setActive(null);
            }}
          >
            <svg className="liquid-transition__crest" viewBox="0 0 1440 150" preserveAspectRatio="none" aria-hidden="true">
              <path className="liquid-transition__ceramic" d="M0 89C151 26 270 120 437 70S704 18 850 72s270 60 390 8c82-35 142-22 200 5v65H0Z" />
              <path className="liquid-transition__cobalt" d="M0 103C151 40 270 134 437 84S704 32 850 86s270 60 390 8c82-35 142-22 200 5v51H0Z" />
            </svg>
            <div className="liquid-transition__body" />
          </div>
          <div className="liquid-transition__label" aria-hidden="true">
            <span>{active.direction === 'returning' ? 'Back to' : 'Next up'}</span>
            <strong>{active.label}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}
